import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { AUTH_ARTWORK, pickInitialArtworkIndex } from '../../config/authArtwork';
import AuthArtworkPanel from './AuthArtworkPanel';
import styles from './AuthShell.module.css';

export const AUTH_TAGLINE = 'Verify what you watched. Build your collection.';

/**
 * AuthShell
 *
 * Two-panel cinematic layout used by every auth page:
 *  - large screens: artwork panel (left) + auth panel (right)
 *  - small screens: artwork hero (top) + auth panel (below)
 *
 * Purely presentational: forms, handlers and validation stay in the pages.
 * Emits `auth_screen_viewed` once per mount.
 */
export default function AuthShell({
    mode = 'login',
    title,
    subtitle,
    pageTitle,
    children,
    footer,
    compact = false,
    clarityMask = false,
}) {
    const [initialIndex] = useState(() => pickInitialArtworkIndex(AUTH_ARTWORK.length));
    const artworkIdRef = useRef(AUTH_ARTWORK[initialIndex]?.id || null);
    const viewedRef = useRef(false);

    // Fire once per mount with the artwork that was visible at first paint.
    useEffect(() => {
        if (viewedRef.current) return;
        viewedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.AUTH_SCREEN_VIEWED, {
            platform: 'web',
            mode,
            artwork_id: artworkIdRef.current || 'none',
        });
    }, [mode]);

    const handleArtworkChange = useCallback((id) => {
        if (!viewedRef.current) artworkIdRef.current = id;
    }, []);

    return (
        <div
            className={`${styles.shell} ${compact ? styles.shellCompact : ''}`}
            data-auth-mode={mode}
            {...(clarityMask ? { 'data-clarity-mask': 'true' } : {})}
        >
            {pageTitle && (
                <Helmet>
                    <title>{pageTitle}</title>
                </Helmet>
            )}

            <AuthArtworkPanel
                initialIndex={initialIndex}
                onArtworkChange={handleArtworkChange}
                showSupportingLine={!compact}
            />

            <main className={styles.authPanel} id="auth-main">
                <div className={styles.authCard}>
                    <div className={styles.accentBar} aria-hidden="true" />

                    <header className={styles.authHeader}>
                        <h1 className={styles.title}>{title}</h1>
                        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                    </header>

                    {children}
                </div>

                {footer && <div className={styles.authFooter}>{footer}</div>}
            </main>
        </div>
    );
}

export function AuthDivider({ label = 'or continue with email' }) {
    return (
        <div className={styles.divider} role="separator" aria-label={label}>
            <span className={styles.dividerLine} aria-hidden="true" />
            <span className={styles.dividerText} aria-hidden="true">{label}</span>
            <span className={styles.dividerLine} aria-hidden="true" />
        </div>
    );
}
